import { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import {
  ArrowLeft, Key, Copy, Check, Trash, Plus, Code, Terminal,
  Globe, ChartLine, MapPin, Lightning, CloudSun, Tree,
} from "@phosphor-icons/react";
import { useAuth } from "@/contexts/AuthContext";
import Footer from "@/components/Footer";
import Navbar from "@/components/Navbar";
import SubscribePlans from "@/components/SubscribePlans";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const GROUP_ICONS = {
  "Vegetation & Carbon": Tree,
  "Climate & Weather": CloudSun,
  "Disasters": Lightning,
  "Space": Globe,
  "Account": ChartLine,
};

export default function DeveloperPage() {
  const { user } = useAuth();
  const [docs, setDocs] = useState(null);
  const [keys, setKeys] = useState([]);
  const [newKeyName, setNewKeyName] = useState("");
  const [createdKey, setCreatedKey] = useState(null);
  const [activeEndpoint, setActiveEndpoint] = useState(null);
  const [lang, setLang] = useState("curl");
  const [copied, setCopied] = useState("");

  useEffect(() => {
    axios.get(`${API}/v1/docs`).then((r) => setDocs(r.data));
  }, []);

  useEffect(() => {
    if (user) {
      axios.get(`${API}/developer/keys`, { withCredentials: true }).then((r) => setKeys(r.data?.keys || []));
    }
  }, [user, createdKey]);

  const groups = useMemo(() => {
    if (!docs?.endpoints) return {};
    return docs.endpoints.reduce((acc, ep) => {
      const g = ep.group || "Other";
      (acc[g] = acc[g] || []).push(ep);
      return acc;
    }, {});
  }, [docs]);

  useEffect(() => {
    if (docs?.endpoints && !activeEndpoint) setActiveEndpoint(docs.endpoints[0]);
  }, [docs, activeEndpoint]);

  const createKey = async () => {
    try {
      const r = await axios.post(`${API}/developer/keys`, { name: newKeyName || "Default" }, { withCredentials: true });
      setCreatedKey(r.data);
      setNewKeyName("");
    } catch (e) {
      alert(e.response?.data?.detail || "Failed to create key");
    }
  };

  const revokeKey = async (id) => {
    if (!window.confirm("Revoke this API key? Applications using it will stop working immediately.")) return;
    await axios.post(`${API}/developer/keys/${id}/revoke`, {}, { withCredentials: true });
    setKeys(keys.filter((k) => k.id !== id));
  };

  const copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text);
    setCopied(label);
    setTimeout(() => setCopied(""), 1500);
  };

  return (
    <div className="min-h-screen bg-stone-50" data-testid="developer-page">
      <Navbar />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-32 pb-20">
        {/* Hero */}
        <section className="mb-16" data-testid="dev-hero">
          <Link to="/" className="inline-flex items-center gap-2 text-sm text-stone-500 hover:text-emerald-700 mb-6">
            <ArrowLeft size={14} weight="bold" /> Back to home
          </Link>
          <p className="text-xs tracking-[0.25em] uppercase font-semibold text-emerald-700 mb-3 font-body">
            For Developers
          </p>
          <h1 className="font-heading text-4xl sm:text-5xl tracking-tight font-medium text-stone-900 mb-4 max-w-3xl">
            Build with the same earth data we use.
          </h1>
          <p className="font-body text-base text-stone-500 max-w-2xl leading-relaxed">
            One API key gives you programmatic access to {docs?.endpoints?.length || "14+"} live earth-monitoring endpoints —
            NDVI, carbon credits, earthquakes, weather, air quality, ISS position, asteroid feeds, verified carbon projects, and more.
          </p>
          <div className="mt-6 flex gap-3 flex-wrap items-center">
            {user ? (
              <a href="#keys" className="bg-emerald-700 hover:bg-emerald-800 text-white text-sm font-medium px-5 py-2.5 rounded-full" data-testid="dev-get-key-cta">
                Get your API key
              </a>
            ) : (
              <Link to="/login" className="bg-emerald-700 hover:bg-emerald-800 text-white text-sm font-medium px-5 py-2.5 rounded-full" data-testid="dev-signin-cta">
                Sign in to get an API key
              </Link>
            )}
            <a href="#endpoints" className="text-sm text-stone-600 hover:text-stone-900 px-4 py-2.5">
              Browse endpoints →
            </a>
          </div>
        </section>

        {/* Keys section */}
        <section id="keys" className="mb-16 bg-white rounded-3xl border border-stone-200 p-6 lg:p-8" data-testid="dev-keys-section">
          <div className="flex items-center gap-3 mb-1">
            <Key size={22} weight="duotone" className="text-emerald-700" />
            <h2 className="font-heading text-xl font-semibold text-stone-900">Your API Keys</h2>
          </div>
          <p className="text-sm text-stone-500 mb-5 font-body">
            Generate one or more keys for different applications. Revoke instantly when needed.
          </p>

          {!user ? (
            <div className="bg-stone-50 border border-dashed border-stone-300 rounded-2xl p-6 text-center">
              <p className="text-sm text-stone-600 mb-3">Sign in to create and manage your API keys.</p>
              <Link to="/login" className="text-sm bg-emerald-700 text-white px-4 py-2 rounded-full">
                Sign in
              </Link>
            </div>
          ) : (
            <>
              {createdKey && (
                <div className="bg-emerald-50 border border-emerald-300 rounded-2xl p-4 mb-4" data-testid="dev-new-key-banner">
                  <p className="text-xs font-semibold text-emerald-900 uppercase tracking-wider mb-2">
                    Key Created — copy it now, you won&apos;t see it again
                  </p>
                  <div className="flex items-center gap-2 bg-white rounded-lg px-3 py-2 border border-emerald-200">
                    <code className="text-xs text-stone-900 flex-1 font-mono break-all" data-testid="dev-new-key-value">{createdKey.api_key}</code>
                    <button
                      onClick={() => copyToClipboard(createdKey.api_key, "new-key")}
                      className="text-emerald-700 hover:text-emerald-900"
                      data-testid="dev-copy-new-key"
                    >
                      {copied === "new-key" ? <Check size={14} weight="bold" /> : <Copy size={14} />}
                    </button>
                  </div>
                  <button onClick={() => setCreatedKey(null)} className="text-xs text-stone-500 mt-2 hover:text-stone-900">
                    Dismiss
                  </button>
                </div>
              )}

              <div className="flex gap-2 mb-5">
                <input
                  type="text" value={newKeyName} onChange={(e) => setNewKeyName(e.target.value)}
                  placeholder="Key name (e.g. 'My iOS App')"
                  className="flex-1 px-4 py-2.5 border border-stone-200 rounded-xl text-sm focus:outline-none focus:border-emerald-500"
                  data-testid="dev-key-name-input"
                />
                <button
                  onClick={createKey}
                  className="bg-emerald-700 hover:bg-emerald-800 text-white text-sm font-medium px-5 py-2.5 rounded-xl flex items-center gap-1.5"
                  data-testid="dev-create-key-btn"
                >
                  <Plus size={14} weight="bold" /> Create Key
                </button>
              </div>

              {keys.length === 0 ? (
                <p className="text-sm text-stone-400 italic" data-testid="dev-no-keys">No keys yet.</p>
              ) : (
                <div className="space-y-2" data-testid="dev-keys-list">
                  {keys.map((k) => (
                    <div key={k.id} className="flex items-center gap-3 bg-stone-50 rounded-xl px-4 py-3 border border-stone-200" data-testid={`dev-key-${k.id}`}>
                      <Key size={14} className="text-stone-400" />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-stone-900 truncate">{k.name}</p>
                        <p className="text-xs text-stone-500 font-mono">{k.key} · {k.requests_count} requests</p>
                      </div>
                      <button
                        onClick={() => revokeKey(k.id)}
                        className="text-red-600 hover:bg-red-50 p-2 rounded-lg"
                        data-testid={`dev-revoke-${k.id}`}
                      >
                        <Trash size={14} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </section>

        {/* Subscription plans */}
        <section className="mb-16" data-testid="dev-plans-section">
          <SubscribePlans user={user} />
        </section>

        {/* Quick start */}
        <section className="mb-16" data-testid="dev-quickstart">
          <h2 className="font-heading text-2xl font-semibold text-stone-900 mb-2">Quick Start</h2>
          <p className="text-sm text-stone-500 mb-5 font-body">
            Send your key in the <code className="bg-stone-100 px-1.5 py-0.5 rounded text-xs">X-API-Key</code> header on every request.
          </p>
          <div className="bg-stone-900 rounded-2xl overflow-hidden border border-stone-800">
            <div className="flex items-center gap-1 bg-stone-800/50 px-3 py-2 border-b border-stone-700">
              {["curl", "javascript", "python"].map((l) => (
                <button
                  key={l} onClick={() => setLang(l)}
                  className={`text-xs px-3 py-1 rounded ${lang === l ? "bg-stone-900 text-white" : "text-stone-400 hover:text-white"}`}
                  data-testid={`dev-quickstart-lang-${l}`}
                >
                  {l}
                </button>
              ))}
            </div>
            <pre className="text-xs text-emerald-300 p-4 overflow-x-auto font-mono" data-testid="dev-quickstart-code">
              {QUICKSTART_SNIPPETS[lang]}
            </pre>
          </div>
        </section>

        {/* Endpoint browser */}
        <section id="endpoints" className="mb-16" data-testid="dev-endpoints-section">
          <h2 className="font-heading text-2xl font-semibold text-stone-900 mb-2">Endpoints</h2>
          <p className="text-sm text-stone-500 mb-6 font-body">
            All endpoints return JSON. Base URL: <code className="bg-stone-100 px-1.5 py-0.5 rounded text-xs">{window.location.origin}/api/v1</code>
          </p>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Sidebar */}
            <aside className="lg:col-span-1 space-y-4 max-h-[600px] overflow-y-auto pr-2">
              {Object.entries(groups).map(([group, eps]) => {
                const Icon = GROUP_ICONS[group] || Code;
                return (
                  <div key={group}>
                    <p className="text-[10px] uppercase tracking-wider text-stone-400 font-semibold mb-1.5 flex items-center gap-1.5">
                      <Icon size={12} weight="duotone" className="text-emerald-700" /> {group}
                    </p>
                    {eps.map((ep, i) => {
                      const isActive = activeEndpoint?.path === ep.path;
                      return (
                        <button
                          key={i} onClick={() => setActiveEndpoint(ep)}
                          className={`w-full text-left px-3 py-2 rounded-lg mb-1 transition-colors ${
                            isActive ? "bg-emerald-700 text-white" : "hover:bg-stone-100"
                          }`}
                          data-testid={`dev-endpoint-${ep.path.replace(/[^a-z0-9]/gi, "-")}`}
                        >
                          <code className={`text-xs font-mono block truncate ${isActive ? "text-white" : "text-stone-900"}`}>
                            {ep.path.replace("/api/v1", "")}
                          </code>
                        </button>
                      );
                    })}
                  </div>
                );
              })}
            </aside>

            {/* Detail */}
            <div className="lg:col-span-2 bg-white rounded-3xl border border-stone-200 p-6 lg:p-8">
              {activeEndpoint ? (
                <EndpointDetail
                  ep={activeEndpoint}
                  lang={lang}
                  setLang={setLang}
                  copy={copyToClipboard}
                  copied={copied}
                />
              ) : (
                <p className="text-stone-500 text-sm">Select an endpoint to view details.</p>
              )}
            </div>
          </div>
        </section>

        {/* Rate limits */}
        <section className="bg-stone-900 rounded-3xl p-8 text-stone-300" data-testid="dev-info-section">
          <h2 className="font-heading text-xl font-semibold text-white mb-4">Limits & Best Practices</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-sm">
            <div>
              <p className="text-stone-500 text-[10px] uppercase tracking-wider mb-1">Rate Limit</p>
              <p className="text-white text-base font-medium">1,000 requests/day</p>
              <p className="text-stone-500 text-xs mt-1">Per key, soft limit. Need more? Contact us.</p>
            </div>
            <div>
              <p className="text-stone-500 text-[10px] uppercase tracking-wider mb-1">Authentication</p>
              <p className="text-white text-base font-medium font-mono">X-API-Key header</p>
              <p className="text-stone-500 text-xs mt-1">Never expose keys in client-side code.</p>
            </div>
            <div>
              <p className="text-stone-500 text-[10px] uppercase tracking-wider mb-1">Errors</p>
              <p className="text-white text-base font-medium">401 / 400 / 500</p>
              <p className="text-stone-500 text-xs mt-1">All errors return JSON {`{"detail": "..."}`}</p>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}

function EndpointDetail({ ep, lang, setLang, copy, copied }) {
  const baseUrl = `${window.location.origin}${ep.path}`;
  const exampleParams = useMemo(() => {
    if (!ep.params || typeof ep.params !== "object") return "";
    const sample = {
      lat: -1.286, lon: 36.817, area_km2: 1, min_magnitude: 4.0, hours: 24,
      days: 14, start_date: "2026-01-01", end_date: "2026-01-15", limit: 10,
      include_forecast: "false", data_type: "temperature", status: "listed",
      category: "wildfires",
    };
    return Object.keys(ep.params).map((p) => `${p}=${sample[p] ?? ""}`).join("&");
  }, [ep]);
  const fullUrl = exampleParams ? `${baseUrl}?${exampleParams}` : baseUrl;

  const snippets = {
    curl: `curl "${fullUrl}" \\\n  -H "X-API-Key: YOUR_API_KEY"`,
    javascript: `const res = await fetch("${fullUrl}", {\n  headers: { "X-API-Key": "YOUR_API_KEY" }\n});\nconst data = await res.json();`,
    python: `import requests\nres = requests.get(\n  "${fullUrl}",\n  headers={"X-API-Key": "YOUR_API_KEY"}\n)\ndata = res.json()`,
  };

  return (
    <div data-testid="dev-endpoint-detail">
      <div className="flex items-center gap-2 mb-3">
        <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded bg-emerald-100 text-emerald-800 font-mono">
          {ep.method || "GET"}
        </span>
        <code className="text-sm font-mono text-stone-900 flex-1 truncate" data-testid="dev-endpoint-path">{ep.path}</code>
      </div>
      <p className="text-sm text-stone-600 mb-5 leading-relaxed">{ep.description}</p>

      {ep.params && typeof ep.params === "object" && Object.keys(ep.params).length > 0 && (
        <div className="mb-5">
          <p className="text-[11px] uppercase tracking-wider text-stone-400 mb-2 font-body">Parameters</p>
          <div className="bg-stone-50 rounded-xl border border-stone-200 overflow-hidden">
            {Object.entries(ep.params).map(([k, v], i) => (
              <div key={k} className={`flex gap-3 px-4 py-2.5 text-xs ${i > 0 ? "border-t border-stone-200" : ""}`}>
                <code className="font-mono text-emerald-700 w-24 flex-shrink-0">{k}</code>
                <span className="text-stone-600">{v}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div>
        <div className="flex items-center justify-between mb-2">
          <p className="text-[11px] uppercase tracking-wider text-stone-400 font-body flex items-center gap-1.5">
            <Terminal size={12} /> Example
          </p>
          <div className="flex gap-1 text-xs">
            {["curl", "javascript", "python"].map((l) => (
              <button key={l} onClick={() => setLang(l)}
                className={`px-2 py-0.5 rounded ${lang === l ? "bg-stone-900 text-white" : "text-stone-500 hover:text-stone-900"}`}
                data-testid={`dev-detail-lang-${l}`}>
                {l}
              </button>
            ))}
          </div>
        </div>
        <div className="bg-stone-900 rounded-xl overflow-hidden relative">
          <button
            onClick={() => copy(snippets[lang], `ep-${ep.path}`)}
            className="absolute top-2 right-2 text-stone-400 hover:text-white text-xs flex items-center gap-1 bg-stone-800 px-2 py-1 rounded"
            data-testid="dev-copy-example"
          >
            {copied === `ep-${ep.path}` ? <><Check size={11} /> Copied</> : <><Copy size={11} /> Copy</>}
          </button>
          <pre className="text-xs text-emerald-300 p-4 overflow-x-auto font-mono" data-testid="dev-example-code">
            {snippets[lang]}
          </pre>
        </div>
      </div>
    </div>
  );
}

const QUICKSTART_SNIPPETS = {
  curl: `# Get current weather for Nairobi
curl "https://conexus.io/api/v1/weather?lat=-1.286&lon=36.817" \\
  -H "X-API-Key: YOUR_API_KEY"`,
  javascript: `// Get current weather for Nairobi
const res = await fetch(
  "https://conexus.io/api/v1/weather?lat=-1.286&lon=36.817",
  { headers: { "X-API-Key": "YOUR_API_KEY" } }
);
const data = await res.json();
console.log(data.current);`,
  python: `# Get current weather for Nairobi
import requests

res = requests.get(
    "https://conexus.io/api/v1/weather",
    params={"lat": -1.286, "lon": 36.817},
    headers={"X-API-Key": "YOUR_API_KEY"},
)
print(res.json()["current"])`,
};
